import { fromMarkdown } from 'mdast-util-from-markdown'
import { NuxtLink } from '#components'

interface Heading {
  type: 'heading'
  depth: number
  children: [PhrasingContent]
}

interface Paragraph {
  type: 'paragraph'
  value?: string
  children: [PhrasingContent]
}

interface Strong {
  type: 'strong'
  value?: string
  children: [PhrasingContent]
}

interface Emphasis {
  type: 'emphasis'
  value?: string
  children: [PhrasingContent]
}

interface Link {
  type: 'link'
  url: string
  title?: string
  value?: string
  children: [StaticPhrasingContent]
}

interface Text {
  type: 'text'
  value?: string
}

interface List {
  type: 'list'
  ordered?: boolean
  start?: number
  spread?: boolean
  children: ListItem[]
}

interface ListItem {
  type: 'listItem'
  spread?: boolean
  value?: string
  children: [PhrasingContent]
}

interface Image {
  type: 'image'
  url: string
  title?: string
  alt?: string
  value?: string
}

interface Break {
  type: 'break'
  value?: string
}

interface Blockquote {
  type: 'blockquote'
  children: [FlowContent]
}

type FlowContent = Blockquote | Heading | List

type StaticPhrasingContent = Break | Emphasis | Image | Strong | Text

type PhrasingContent = Link | StaticPhrasingContent

const renderMachine: Record<string, any> = {
  paragraph(node: Paragraph) {
    const children = renderMachine.childnodes(node.children)
    return h('p', null, { default: () => children ?? '' })
  },

  link(node: Link) {
    const route = useRoute()
    // to detect pdfs in links
    const isPdfRegex = /([\w\s\.\:\/-]+\.[pP][dD][fF])/g

    const to = node.url.replace(isPdfRegex, (match, p1) => `/uploads/${p1}`)

    if (node.url.match(isPdfRegex)) {
      node.children[0].value = `${node.children[0].value} (PDF)`
    }

    const children = renderMachine.childnodes(node.children) ?? ''

    return h(
      NuxtLink,
      { to, target: resolveLinkTarget(node.url) },
      { default: () => children }
    )
  },

  heading(node: Heading) {
    const type = `h${node.depth}`
    return h(type, null, { default: () => node.children[0].value ?? '' })
  },

  strong(node: Strong) {
    return h('strong', null, { default: () => node.children[0].value ?? '' })
  },

  emphasis(node: Emphasis) {
    return h('em', null, { default: () => node.children[0].value ?? '' })
  },

  text(node: Text) {
    return node.value ?? ''
  },

  list(node: List) {
    const type = node.ordered ? 'ol' : 'ul'
    const children = renderMachine.childnodes(node.children)
    return h(type, null, { default: () => children ?? '' })
  },

  listItem(node: ListItem) {
    const children = renderMachine.childnodes(node.children)
    return h('li', null, { default: () => children })
  },

  image(node: Image) {
    return h('img', { src: node.url, alt: node.alt ?? '' })
  },

  blockquote(node: Blockquote) {
    const children = renderMachine.childnodes(node.children)
    return h('blockquote', null, { default: () => children })
  },

  break(_: Break) {
    return h('br')
  },

  default(_: Text) {
    return ''
  },

  childnodes: function <
    Type extends FlowContent | StaticPhrasingContent | PhrasingContent
  >(children: Type[]): Type[] {
    return children.map((node: Type) => {
      return (renderMachine[node.type] ?? renderMachine.default)(node as Type)
    })
  },
}

export const mdastRenderer = function (md?: string) {
  if (md) {
    const tree = fromMarkdown(md)
    return tree.children.map((child) =>
      (renderMachine[child.type] ?? renderMachine.default)(child)
    )
  }
}
